import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crosshair,
  MapPin,
  ArrowRight,
  Flame,
  Storefront,
  WarningCircle,
  ArrowLeft,
} from "@phosphor-icons/react";
import api, { API, formatApiError } from "../lib/api";
import { getUserLocation, getPermissionState, isLocationDeniedCached } from "../lib/geo";
import { track as trackAnalytics } from "../lib/analytics";
import { etTimeMs, fmtET, etDateKey, etDateKeyFromMs } from "../lib/etTime";
import PageMeta from "../components/PageMeta";
import { toast } from "sonner";

// Radius for "near me" — matches the user's mental model from the
// dashboard nudge copy ("trucks within 2 miles").
const RADIUS_MILES = 2;

function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Format e.g. "0.4 mi" / "1.2 mi". Sub-0.1mi shows "< 0.1 mi" so we never
// show "0.0 mi" which reads like a bug.
function formatMiles(mi) {
  if (mi == null || Number.isNaN(mi)) return "";
  if (mi < 0.1) return "< 0.1 mi";
  return `${mi.toFixed(1)} mi`;
}

// Time helpers — ET-pinned so times match the truck's wall clock.
function timeStatus(t, now) {
  const s = etTimeMs(t.starts_at);
  const e = etTimeMs(t.ends_at);
  if (now >= e) return "past";
  if (now >= s) return "live";
  return "future";
}

function whenLine(t, now) {
  const sMs = etTimeMs(t.starts_at);
  const eMs = etTimeMs(t.ends_at);
  const fmt = (iso) =>
    fmtET(iso, { hour: "numeric", minute: "2-digit" }).replace(":00", "");
  if (now >= eMs) return `Closed ${fmt(t.ends_at)}`;
  if (now >= sMs) return `Open now · until ${fmt(t.ends_at)}`;
  // Future: same ET day → "Opens at 5pm". Different day → "Tomorrow · 11am" or "Sat · 11am".
  const sKey = etDateKey(t.starts_at);
  if (sKey === etDateKeyFromMs(now)) return `Opens at ${fmt(t.starts_at)}`;
  if (sKey === etDateKeyFromMs(now, 1)) return `Tomorrow · ${fmt(t.starts_at)}`;
  return `${fmtET(t.starts_at, { weekday: "short" })} · ${fmt(t.starts_at)}`;
}

function statusBadge(status) {
  if (status === "live") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold px-2 py-0.5"
        data-testid="near-me-card-status-live"
      >
        <Flame weight="fill" className="h-3 w-3" />
        Live
      </span>
    );
  }
  if (status === "future") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground/80 text-[11px] font-semibold px-2 py-0.5"
        data-testid="near-me-card-status-future"
      >
        Upcoming
      </span>
    );
  }
  return null;
}

export default function NearMe() {
  const [phase, setPhase] = useState("idle"); // idle | locating | loading | done | denied | error
  const [pos, setPos] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [now, setNow] = useState(Date.now());

  // Tick clock once a minute so "Live / Upcoming" doesn't go stale while
  // the user lingers on the page.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // If geo was already granted in a previous session, auto-run on mount
  // so the page feels instant for repeat visitors. We never auto-trigger
  // the OS prompt — that requires an explicit tap.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const state = await getPermissionState();
      if (cancelled) return;
      if (state === "granted" && !isLocationDeniedCached()) {
        runLocate({ silent: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLocate = useCallback(
    async ({ silent = false } = {}) => {
      setErrMsg("");
      setPhase("locating");
      trackAnalytics("near_me_locate_click");
      const res = await getUserLocation({ requireInteraction: !silent });
      if (!res.ok) {
        if (res.reason === "denied") {
          setPhase("denied");
        } else if (res.reason === "no_permission") {
          // Silent run hit "needs interaction" — drop quietly back to idle.
          setPhase("idle");
        } else {
          setPhase("error");
          setErrMsg(
            res.reason === "timeout"
              ? "Couldn't get your location in time. Try again?"
              : "Location isn't available on this device.",
          );
        }
        return;
      }
      setPos({ lat: res.lat, lng: res.lng });
      setPhase("loading");
      try {
        const { data } = await api.get("/trucks/upcoming", { params: { when: "week" } });
        setTrucks(Array.isArray(data) ? data : []);
        setPhase("done");
      } catch (e) {
        setPhase("error");
        setErrMsg(formatApiError(e.response?.data?.detail));
        toast.error("Couldn't load trucks. Please try again.");
      }
    },
    [],
  );

  // Filter to within radius, dedupe by truck_id (keep the most relevant
  // event per truck), then sort live-first → soonest-upcoming → distance.
  const nearby = useMemo(() => {
    if (!pos || trucks.length === 0) return [];
    const inRadius = trucks
      .map((t) => ({
        ...t,
        _dist: distanceMiles(pos.lat, pos.lng, t.lat, t.lng),
        _status: timeStatus(t, now),
      }))
      .filter((t) => t._dist <= RADIUS_MILES && t._status !== "past");

    // Dedupe per truck_id: prefer live → soonest upcoming.
    const byTruck = new Map();
    for (const t of inRadius) {
      const key = t.truck_id || t.id;
      const prev = byTruck.get(key);
      if (!prev) {
        byTruck.set(key, t);
        continue;
      }
      const prevRank = prev._status === "live" ? 0 : 1;
      const curRank = t._status === "live" ? 0 : 1;
      if (curRank < prevRank) {
        byTruck.set(key, t);
      } else if (curRank === prevRank) {
        // Same status → keep the one starting sooner.
        if (new Date(t.starts_at) < new Date(prev.starts_at)) {
          byTruck.set(key, t);
        }
      }
    }
    return Array.from(byTruck.values()).sort((a, b) => {
      const ar = a._status === "live" ? 0 : 1;
      const br = b._status === "live" ? 0 : 1;
      if (ar !== br) return ar - br;
      // Within same status, sort by distance ascending.
      if (a._dist !== b._dist) return a._dist - b._dist;
      return new Date(a.starts_at) - new Date(b.starts_at);
    });
  }, [trucks, pos, now]);

  const liveCount = nearby.filter((t) => t._status === "live").length;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="near-me-page">
      <PageMeta
        title="Find food trucks within 2 miles · TruckTrackr"
        description="Tap once to see every food truck currently parked within 2 miles of you. Live status, distance, and walking directions."
        canonical="/nearby"
      />

      <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-8 sm:pt-14 pb-24">
        {/* Back to map */}
        <Link
          to="/map"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition mb-6"
          data-testid="near-me-back-to-map"
        >
          <ArrowLeft className="h-4 w-4" />
          Full map
        </Link>

        {/* Hero / CTA */}
        <div className="text-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1"
            data-testid="near-me-eyebrow"
          >
            <Crosshair weight="fill" className="h-3.5 w-3.5" />
            Within {RADIUS_MILES} miles
          </span>
          <h1
            className="mt-5 font-display font-bold tracking-tighter text-4xl sm:text-5xl leading-[1.05]"
            data-testid="near-me-title"
          >
            What&apos;s parked
            <br />
            <span className="text-primary">right next to me?</span>
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-md mx-auto">
            One tap. We check your location, then show you every food truck within {RADIUS_MILES} miles
            this week — live first, walking distance next.
          </p>

          {/* Primary action — only when idle. After locate runs we let the
              result list speak for itself. */}
          {(phase === "idle" || phase === "locating") && (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => runLocate({ silent: false })}
                disabled={phase === "locating"}
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed h-14 px-7 text-base font-semibold shadow-sm transition active:scale-[0.98]"
                data-testid="near-me-locate-btn"
              >
                <Crosshair weight="fill" className="h-5 w-5" />
                {phase === "locating" ? "Locating you…" : "Find trucks near me"}
                {phase !== "locating" && <ArrowRight className="h-4 w-4" />}
              </button>
              <p className="mt-3 text-xs text-muted-foreground/80">
                We only use your location for this check. Nothing is stored.
              </p>
            </div>
          )}
        </div>

        {/* Denied state */}
        {phase === "denied" && (
          <div
            className="mt-10 rounded-2xl border border-border bg-card p-6 text-center"
            data-testid="near-me-denied"
          >
            <WarningCircle weight="duotone" className="h-10 w-10 text-primary mx-auto" />
            <h2 className="mt-3 font-display font-bold text-xl">Location is off</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We can&apos;t show trucks near you without location access. Enable it in your browser settings,
              then refresh — or browse the full map instead.
            </p>
            <Link to="/map">
              <button
                type="button"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5 text-sm font-semibold transition"
                data-testid="near-me-denied-map-cta"
              >
                <MapPin weight="duotone" className="h-4 w-4" />
                Open the full map
              </button>
            </Link>
          </div>
        )}

        {/* Error state */}
        {phase === "error" && (
          <div
            className="mt-10 rounded-2xl border border-border bg-card p-6 text-center"
            data-testid="near-me-error"
          >
            <WarningCircle weight="duotone" className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="mt-3 font-display font-bold text-xl">Something hiccuped</h2>
            <p className="mt-2 text-sm text-muted-foreground">{errMsg || "Try again in a moment."}</p>
            <button
              type="button"
              onClick={() => runLocate({ silent: false })}
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-border hover:bg-muted h-11 px-5 text-sm font-semibold transition"
              data-testid="near-me-error-retry"
            >
              <Crosshair weight="fill" className="h-4 w-4" />
              Try again
            </button>
          </div>
        )}

        {/* Loading skeleton (after locate, before fetch returns) */}
        {phase === "loading" && (
          <div className="mt-10 space-y-3" data-testid="near-me-loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-20 rounded-2xl bg-muted/60 animate-pulse"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {/* Results */}
        {phase === "done" && (
          <div className="mt-10" data-testid="near-me-results">
            {/* Result summary */}
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display font-bold text-lg" data-testid="near-me-summary">
                {nearby.length === 0
                  ? "Nothing within reach"
                  : `${nearby.length} truck${nearby.length === 1 ? "" : "s"} nearby`}
                {liveCount > 0 && (
                  <span className="ml-2 text-primary text-base font-semibold">
                    · {liveCount} live
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={() => runLocate({ silent: false })}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground transition"
                data-testid="near-me-refresh"
              >
                Refresh
              </button>
            </div>

            {nearby.length === 0 ? (
              <div
                className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center"
                data-testid="near-me-empty"
              >
                <Storefront weight="duotone" className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="mt-4 text-sm text-muted-foreground">
                  No trucks within {RADIUS_MILES} miles this week. The full map shows everything
                  scheduled across the county.
                </p>
                <Link to="/map">
                  <button
                    type="button"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5 text-sm font-semibold transition"
                    data-testid="near-me-empty-map-cta"
                  >
                    <MapPin weight="duotone" className="h-4 w-4" />
                    Browse the full map
                  </button>
                </Link>
              </div>
            ) : (
              <ul className="space-y-3">
                <AnimatePresence initial={false}>
                  {nearby.map((t, i) => {
                    const tid = t.truck_id || t.id;
                    return (
                      <motion.li
                        key={tid}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: i * 0.04 }}
                        data-testid={`near-me-card-${tid}`}
                      >
                        <Link
                          to={`/map?truck=${encodeURIComponent(tid)}`}
                          onClick={() => trackAnalytics("near_me_card_click", tid)}
                          className="group flex items-center gap-4 rounded-2xl border border-border bg-card hover:bg-card/80 hover:border-primary/40 active:scale-[0.99] transition p-4"
                        >
                          {/* Logo / fallback */}
                          <div className="relative h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 grid place-items-center">
                            {t.logo_path ? (
                              <img
                                src={`${API}/files/${t.logo_path}`}
                                alt={t.truck_name}
                                loading="lazy"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Storefront weight="duotone" className="h-7 w-7 text-primary" />
                            )}
                          </div>

                          {/* Copy */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p
                                className="font-semibold truncate"
                                data-testid={`near-me-card-name-${tid}`}
                              >
                                {t.truck_name}
                              </p>
                              {statusBadge(t._status)}
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground truncate">
                              {whenLine(t, now)}
                            </p>
                          </div>

                          {/* Distance chip */}
                          <div className="text-right shrink-0">
                            <div
                              className="text-sm font-bold text-foreground"
                              data-testid={`near-me-card-distance-${tid}`}
                            >
                              {formatMiles(t._dist)}
                            </div>
                            <div className="mt-0.5 inline-flex items-center text-[11px] font-semibold text-muted-foreground group-hover:text-primary transition">
                              View <ArrowRight className="h-3 w-3 ml-0.5" />
                            </div>
                          </div>
                        </Link>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ul>
            )}

            {/* Footer CTA — always show the "Open full map" exit so the
                user has a clean way out into the richer experience. */}
            <div className="mt-8 text-center">
              <Link
                to="/map"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition"
                data-testid="near-me-footer-map"
              >
                Or see everything on the full map
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
